import Visualization from '../visualization';
import Transformation from '../../tabledata/transformation';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isNumericColumn(rows, colIdx) {
  let numeric = 0;
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    const v = rows[i][colIdx];
    if (v !== null && v !== '') {
      total++;
      if (!isNaN(parseFloat(v)) && isFinite(v)) {
        numeric++;
      }
    }
  }
  return total > 0 && numeric / total >= 0.8;
}

function pearson(xs, ys) {
  const n = xs.length;
  if (n < 2) {
    return 0;
  }
  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < n; i++) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let num = 0;
  let denX = 0;
  let denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  if (denX === 0 || denY === 0) {
    return 0;
  }
  return num / Math.sqrt(denX * denY);
}

// ─── Transformation (pass-through, no settings UI) ───────────────────────────

class PassthroughTransformation extends Transformation {
  transform(tableData) {
    return tableData;
  }
}

// ─── Visualization ───────────────────────────────────────────────────────────

export default class CorrelationVisualization extends Visualization {
  constructor(targetEl, config) {
    super(targetEl, config);
    this.transformation = new PassthroughTransformation(config);
    this._tableData = null;
  }

  type() {
    return 'correlationMatrix';
  }

  getTransformation() {
    return this.transformation;
  }

  refresh() {
    if (this._tableData) {
      this._draw(this._tableData);
    }
  }

  render(tableData) {
    this._tableData = tableData;
    this._draw(tableData);
  }

  destroy() {
    this.targetEl.empty();
  }

  _draw(tableData) {
    this.targetEl.empty();

    const cols = tableData.columns;
    const rows = tableData.rows;

    if (rows.length < 2) {
      this.targetEl.html(
        '<div style="padding:24px;color:#999;text-align:center;font-size:13px;">' +
        'Need at least 2 rows to compute correlations.</div>'
      );
      return;
    }

    // ── Find numeric columns ──────────────────────────────────────────────────
    const numericCols = cols.filter(function(col) {
      return isNumericColumn(rows, col.index);
    });

    if (numericCols.length < 2) {
      this.targetEl.html(
        '<div style="padding:24px;color:#999;text-align:center;font-size:13px;">' +
        'Need at least 2 numeric columns to build a correlation matrix.</div>'
      );
      return;
    }

    // ── Extract numeric values ────────────────────────────────────────────────
    const vectors = numericCols.map(function(col) {
      return rows.map(function(row) {
        return parseFloat(row[col.index]) || 0;
      });
    });

    // ── Compute pairwise Pearson correlations ─────────────────────────────────
    const labels = numericCols.map(function(c) {
      return c.name;
    });
    const n = labels.length;
    const matrix = [];
    for (let i = 0; i < n; i++) {
      matrix[i] = [];
      for (let j = 0; j < n; j++) {
        matrix[i][j] = (i === j) ? 1 : pearson(vectors[i], vectors[j]);
      }
    }

    // ── Layout ────────────────────────────────────────────────────────────────
    const margin = {top: 20, right: 20, bottom: 100, left: 100};
    const minHeight = 400;
    if ((this.targetEl.height() || 0) < minHeight) {
      this.targetEl.height(minHeight);
    }
    const totalW = this.targetEl.width() || 500;
    const totalH = this.targetEl.height();
    const side = Math.min(totalW - margin.left - margin.right,
      totalH - margin.top - margin.bottom);
    const w = Math.max(side, 80);
    const h = w; // square matrix

    const svg = d3.select(this.targetEl[0])
      .append('svg')
      .attr('width', w + margin.left + margin.right)
      .attr('height', h + margin.top + margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // ── Scales ────────────────────────────────────────────────────────────────
    const scale = d3.scale.ordinal().domain(labels).rangeBands([0, w], 0.04);
    // Diverging: blue(–1) → white(0) → red(+1)
    const colorScale = d3.scale.linear()
      .domain([-1, 0, 1])
      .range(['#2166ac', '#f7f7f7', '#d6604d'])
      .clamp(true);

    // ── Axes ──────────────────────────────────────────────────────────────────
    svg.append('g')
      .attr('class', 'x axis')
      .attr('transform', 'translate(0,' + h + ')')
      .call(d3.svg.axis().scale(scale).orient('bottom'))
      .selectAll('text')
      .style('fill', '#333')
      .style('font-size', '13px')
      .style('text-anchor', 'end')
      .attr('dx', '-0.8em')
      .attr('dy', '0.15em')
      .attr('transform', 'rotate(-40)');

    svg.append('g')
      .attr('class', 'y axis')
      .call(d3.svg.axis().scale(scale).orient('left'))
      .selectAll('text')
      .style('fill', '#333')
      .style('font-size', '13px');

    // ── Tooltip ───────────────────────────────────────────────────────────────
    const tooltip = d3.select(this.targetEl[0])
      .append('div')
      .style('position', 'absolute')
      .style('background', 'rgba(0,0,0,0.75)')
      .style('color', '#fff')
      .style('padding', '4px 8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    // ── Cells ─────────────────────────────────────────────────────────────────
    const cellSize = scale.rangeBand();
    const cells = [];
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        cells.push({row: labels[i], col: labels[j], r: matrix[i][j]});
      }
    }

    svg.selectAll('.cm-cell')
      .data(cells)
      .enter()
      .append('rect')
      .attr('class', 'cm-cell')
      .attr('x', function(d) {
        return scale(d.col);
      })
      .attr('y', function(d) {
        return scale(d.row);
      })
      .attr('width', cellSize)
      .attr('height', cellSize)
      .style('fill', function(d) {
        return colorScale(d.r);
      })
      .style('stroke', '#fff')
      .style('stroke-width', '1px')
      .on('mouseover', function(d) {
        tooltip.transition().duration(100).style('opacity', 1);
        tooltip.html(
          '<b>' + d.row + '</b> × <b>' + d.col + '</b><br/>' +
          'r = ' + d.r.toFixed(4)
        );
        d3.select(this).style('stroke', '#333').style('stroke-width', '2px');
      })
      .on('mousemove', function() {
        const pos = d3.mouse(svg.node());
        tooltip
          .style('left', (pos[0] + margin.left + 10) + 'px')
          .style('top', (pos[1] + margin.top - 10) + 'px');
      })
      .on('mouseout', function() {
        tooltip.transition().duration(100).style('opacity', 0);
        d3.select(this).style('stroke', '#fff').style('stroke-width', '1px');
      });

    // ── Correlation value labels ──────────────────────────────────────────────
    svg.selectAll('.cm-label')
      .data(cells)
      .enter()
      .append('text')
      .attr('class', 'cm-label')
      .attr('x', function(d) {
        return scale(d.col) + cellSize / 2;
      })
      .attr('y', function(d) {
        return scale(d.row) + cellSize / 2;
      })
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .style('font-size', Math.max(Math.min(cellSize * 0.28, 13), 9) + 'px')
      .style('font-weight', '500')
      // White text on saturated colors (|r| > 0.5), dark text near white center
      .style('fill', function(d) {
        return Math.abs(d.r) > 0.5 ? '#fff' : '#222';
      })
      .text(function(d) {
        return d.r.toFixed(2);
      });

    // ── Diverging color legend ────────────────────────────────────────────────
    const legendW = Math.min(240, Math.floor(w * 0.8));
    const legendX = Math.floor((w - legendW) / 2);
    const legendY = h + margin.bottom - 22;

    const defs = svg.append('defs');
    const grad = defs.append('linearGradient').attr('id', 'cm-legend-grad');
    [
      {o: '0%', c: '#2166ac'},
      {o: '50%', c: '#f7f7f7'},
      {o: '100%', c: '#d6604d'},
    ].forEach(function(s) {
      grad.append('stop').attr('offset', s.o).attr('stop-color', s.c);
    });

    const legendG = svg.append('g')
      .attr('transform', 'translate(' + legendX + ',' + legendY + ')');

    legendG.append('rect')
      .attr('width', legendW).attr('height', 8)
      .style('fill', 'url(#cm-legend-grad)');

    const legendScale = d3.scale.linear().domain([-1, 1]).range([0, legendW]);
    legendG.append('g')
      .attr('transform', 'translate(0,8)')
      .call(d3.svg.axis().scale(legendScale).orient('bottom')
        .tickValues([-1, -0.5, 0, 0.5, 1])
        .tickFormat(d3.format('.1f')))
      .selectAll('text')
      .style('fill', '#333')
      .style('font-size', '11px');
    legendG.select('.domain').style('display', 'none');
  }
}
